import React, { useEffect, useMemo, useState } from "react"
import moment from "moment"

import { Clock, MessageSquare, Plus, X } from "lucide-react"
import { ReactComponent as Expend } from "components/icons/svgs/expend.svg"
import { ReactComponent as Collapsed } from "components/icons/svgs/collased.svg"

import "./TableSituation.scss"
import { IconButton, InputAdornment, Popover, TextField } from "@mui/material"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import { useTreasureHunt } from "components/useTreasureHunt/useTreasureHunt"
import { HScroll } from "procurement/design/components/HScroll"
import FilterableHeaderCell from "./FilterControls"
import { applyColumnFilters, getActiveFilterCount } from "./tableFilters"
import { formatSummaryMetric } from "components/ClassSituationLXO/classSituationMappers"
import { CLASS_SITUATION_WEEKS } from "components/ClassSituationLXO/classSituationExportSpec"
import { moduleColumnKey, weekColumnKey } from "components/ClassSituationLXO/classSituationColumnTree"
import { sortClassSituationRows } from "components/ClassSituationLXO/classSituationSort"

function isInCurrentMonth(dateStr) {
  if (!dateStr) return false
  const parts = dateStr.split("/")
  const [day, month, year] = parts.map(Number)
  const safeYear =
    Number.isFinite(year) && parts.length >= 3 ? year : new Date().getFullYear()
  const inputDate = new Date(safeYear, month - 1, day)
  const today = new Date()
  return (
    inputDate.getFullYear() === today.getFullYear() &&
    inputDate.getMonth() === today.getMonth()
  )
}

const showValue = (value) =>
  value === null || value === undefined || value === "" ? "-" : value

const showArray = (value) =>
  Array.isArray(value) && value.length ? value.join(", ") : "-"

// Base URL for the Hub class-detail page. The backend returns the raw
// classId which gets appended to the end of this path.
const HUB_CLASS_DETAIL_BASE = "https://hub24.apollo.vn:8888/class/detail/"

const renderClassNameLink = (name, classId) => {
  if (classId === null || classId === undefined || classId === "") return showValue(name)
  return (
    <a
      className="hub-link"
      href={`${HUB_CLASS_DETAIL_BASE}${classId}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {showValue(name)}
    </a>
  )
}

 const renderRecommendToClose = (value) => {
      const normalized = String(value ?? "")
        .trim()
        .toLowerCase()
      if (!normalized) return "-"
      const isYes = normalized === "yes" || normalized === "true"
      return (
        <span className={`class-table-status ${isYes ? "recommend-yes" : "recommend-no"}`}>
          {isYes ? "Yes" : "No"}
        </span>
      )
    }

const WEEK_LEAF_WIDTH = 90
const leafWidth = (leaf) => leaf.width ?? WEEK_LEAF_WIDTH
const weekWidth = (week) => week.leaves.reduce((sum, leaf) => sum + leafWidth(leaf), 0)
const MODULE_KEYS = ["a", "b"]
const MODULE_WIDTH = 240
const EVENT_LEAF_WIDTH = 150
const EVENT_COLUMNS = [
  { key: "eoccDate", label: "EOCC date", value: (row) => row.tests?.eoccDate },
  { key: "moduleTestDate", label: "Module test date", value: (row) => row.tests?.moduleTestDate },
  { key: "unitTestDate", label: "Unit test date", value: (row) => row.tests?.unitTestDate },
]
const weekLeafGrid = (week) => ({
  gridTemplateColumns: week.leaves.map((leaf) => `${leafWidth(leaf)}px`).join(" "),
})

// Fill window closes at the midpoint of the current module (BE: tests.finalToFill).
const isPastFinalFillDate = (date) => !!date && moment().format("YYYY-MM-DD") > date

// Footer metrics below must stay aligned with the CSV export
// (classSituationExportSpec.js + classSituationExport.js). Update both together.
const SCHEDULE_SUMMARY = {
  eppThisMonth: { key: "totalThisMonthEpp", kind: "total" },
  eppNextMonth: { key: "totalNextMonthEpp", kind: "total" },
  eppT8: { key: "totalNext2MonthEpp", kind: "total" },
  estimateAcsThisMonth: { key: "avgEstimateAcsThisMonth", kind: "avg" },
  estimateAcsNextMonth: { key: "avgEstimateAcsNextMonth", kind: "avg" },
  estimateAcsNext2Month: { key: "avgEstimateAcsNext2Month", kind: "avg" },
}

const LEGEND_ITEMS = [
  {
    swatch: { background: "#fee2e2", boxShadow: "inset 3px 0 0 #fca5a5" },
    text: "Red row — the class still has open seats, but the last day to add students to the current module has passed",
  },
  {
    swatch: { color: "#d32f2f", fontWeight: 700 },
    sample: "12/10",
    text: "Red date (Final fill date) — the last day to add students has passed",
  },
  {
    swatch: { color: "#ef4444" },
    sample: "05/11",
    text: "Red module date — the module starts or finishes this month",
  },
  { swatch: { background: "#ecfdf5", color: "#0f766e" }, sample: "In progress", text: "Class is running" },
  { swatch: { background: "#fffbeb", color: "#b45309" }, sample: "Planning", text: "Class has not started yet" },
  { swatch: { background: "#fef2f2", color: "#ef4444" }, sample: "Full", text: "Class is full, closed or finished" },
  { sample: "SUM / AVG", text: "First row — total (SUM) or average (AVG) of all classes shown" },
]

function TableLegend() {
  const [open, setOpen] = useState(false)
  return (
    <div className="table-legend">
      <button type="button" className="table-legend__toggle" onClick={() => setOpen(!open)}>
        {open ? "Hide legend" : "Legend"}
      </button>
      {open && (
        <ul className="table-legend__list">
          {LEGEND_ITEMS.map((item) => (
            <li key={item.text}>
              <span className="table-legend__swatch" style={item.swatch}>
                {item.sample ?? ""}
              </span>
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function SummaryMetric({ kind, value, hint }) {
  return (
    <span className={`summary-metric summary-metric--${kind === "avg" ? "avg" : "total"}`}>
      <span className="summary-metric__kind">{kind === "avg" ? "AVG" : "SUM"}</span>
      {hint ? <span className="summary-metric__hint">{hint}</span> : null}
      <span className="summary-metric__value">{formatSummaryMetric(value, kind)}</span>
    </span>
  )
}

export default function TableSituation({ table, onAddNote }) {
  const [openModule, setOpenModule] = useState(false)
  const [openEvent, setOpenEvent] = useState(true)
  const [openByWeek, setOpenByWeek] = useState(true)
  const [openSchedule, setOpenSchedule] = useState(true)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null })
  const { triggerIfNew } = useTreasureHunt("assistant-add-note")

  const ROW_COLOR_MAP = {
    red: { bg: "#fee2e2", border: "#fca5a5" },
    yellow: { bg: "#fef9c3", border: "#fde047" },
    green: { bg: "#dcfce7", border: "#86efac" },
  }

  const isPossibleToFill = (fillStatus) =>
    String(fillStatus ?? "").trim().toLowerCase() === "possible to fill"

  const getComputedRowColor = (row) =>
    isPossibleToFill(row?.fillStatus) && isPastFinalFillDate(row?.finalFillDate) ? "red" : null

  const getRowColorTitle = (rowColor) =>
    rowColor === "red" ? "Past the final date to fill students of the current module" : undefined

  const getRowStyle = (row) => {
    const color = ROW_COLOR_MAP[getComputedRowColor(row)]
    if (!color) return {}
    return {
      backgroundColor: color.bg,
      // Inset shadow instead of border-left so the row isn't pushed 3px out of column alignment.
      boxShadow: `inset 3px 0 0 ${color.border}`,
    }
  }

  const normalizeNotes = (row) => {
    if (Array.isArray(row.notes) && row.notes.length > 0) return row.notes
    const text = row?.nextMonth?.notes
    if (text === null || text === undefined || text === "") return []
    return [
      {
        id: crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2),
        text,
        timestamp: "",
        user: "",
      },
    ]
  }

  // ✅ Local rows state so we can store notes per-row
  const [rows, setRows] = useState(() => {
    const input = Array.isArray(table?.rows) ? table.rows : []
    // Ensure each row has stable id + notes array
    return input.map((r, idx) => ({
      ...r,
      id: r.id ?? `${r.classCode ?? "row"}-${idx}`,
      notes: normalizeNotes(r),
    }))
  })

  useEffect(() => {
    const input = Array.isArray(table?.rows) ? table.rows : []
    setRows(
      input.map((r, idx) => ({
        ...r,
        id: r.id ?? `${r.classCode ?? "row"}-${idx}`,
        notes: normalizeNotes(r),
      }))
    )
  }, [table?.rows])

  const [newNote, setNewNote] = useState("")
  const [anchorEl, setAnchorEl] = useState(null)
  const [activeRowId, setActiveRowId] = useState(null)
  const open = Boolean(anchorEl)

  // filters: { [columnKey]: Set<string> } — a column is only present here
  // when the user deselected at least one of its values.
  const [filters, setFilters] = useState({})
  const summary = table?.summary ?? null

  const setColumnFilter = (key, selected) => {
    setFilters((prev) => {
      if (selected === null) {
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: selected }
    })
  }

  const clearAllFilters = () => setFilters({})

  const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [filters])

  const filteredRows = useMemo(() => applyColumnFilters(rows, filters), [rows, filters])

  const activeNotes = useMemo(() => {
    if (!activeRowId) return []
    return rows.find((r) => r.id === activeRowId)?.notes || []
  }, [rows, activeRowId])

  const checkExistColumn = (accessor) =>
    Array.isArray(table?.columns) &&
    table.columns.some((col) => col.accessor === accessor)

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" }
      if (prev.direction === "asc") return { key, direction: "desc" }
      return { key: null, direction: null }
    })
  }

  const renderHeaderLabel = (label, key, options = {}) => (
    <FilterableHeaderCell
      label={label}
      filterKey={key}
      sortable={options.sortable !== false}
      sortConfig={sortConfig}
      onToggleSort={toggleSort}
      rows={rows}
      filters={filters}
      onApplyFilter={setColumnFilter}
    />
  )

  const getSortableValue = (row, key) => {
    const raw = row?.[key]
    if (raw === null || raw === undefined) return ""
    if (Array.isArray(raw)) return raw.join(", ")
    return typeof raw === "number" ? raw : String(raw)
  }

  const fixedWidthStyle = (width) => ({
    width,
    flex: `0 0 ${width}px`,
  })

  const visibleModuleKeys = MODULE_KEYS.filter((m) => checkExistColumn(moduleColumnKey(m)))
  const moduleGroupWidth = visibleModuleKeys.length * MODULE_WIDTH
  const moduleGrid = { gridTemplateColumns: `repeat(${visibleModuleKeys.length}, minmax(0, 1fr))` }

  const eventColumns = EVENT_COLUMNS.filter((col) => checkExistColumn(col.key))
  const eventGroupWidth = eventColumns.length * EVENT_LEAF_WIDTH
  const eventGrid = { gridTemplateColumns: `repeat(${eventColumns.length}, minmax(0, 1fr))` }

  const visibleWeeks = CLASS_SITUATION_WEEKS.map((week) => ({
    ...week,
    leaves: week.leaves.filter((leaf) => checkExistColumn(weekColumnKey(week.key, leaf.label))),
  })).filter((week) => week.leaves.length)
  const weekGroupWidth = visibleWeeks.reduce((sum, week) => sum + weekWidth(week), 0)
  const weekGridTemplate = visibleWeeks.map((week) => `${weekWidth(week)}px`).join(" ")

  const renderClassStatus = (id) => {
    if (id === null || id === undefined || id === "") return "-"
    const label =
      id === 1
        ? "Planning"
        : id === 2
        ? "In progress"
        : id === "Full"
        ? "Full"
        : typeof id === "string" && id.trim()
        ? id.trim()
        : "Possible to fill"
    const key = label.toLowerCase()
    const tone =
      key === "planning"
        ? "planning"
        : key === "full" || key === "closed" || key === "finished"
        ? "close"
        : "in-progress"
    return <span className={`class-table-status ${tone}`}>{label}</span>
  }

  const scheduleColumns = [
    {
      key: "schedule",
      label: "Schedule",
      width: 200,
      cellClass: "schedule",
      render: (row) =>
        row.schedule
          ? row.schedule.split(" / ").map((item, idx, arr) => (
              <React.Fragment key={`${item}-${idx}`}>
                <Clock size={12} color="#94a3b8" /> {item}
                {idx < arr.length - 1 && <br />}
              </React.Fragment>
            ))
          : "-",
    },
    {
      key: "status",
      label: "Class status",
      width: 130,
      render: (row) => renderClassStatus(row.status),
    },
    {
      key: "estimateAcsThisMonth",
      label: "Estimated ACS This Month",
      width: 120,
      numeric: true,
      render: (row) => showValue(row.estimateAcsThisMonth),
    },
    {
      key: "estimateAcsNextMonth",
      label: "Estimated ACS Next Month",
      width: 120,
      numeric: true,
      render: (row) => showValue(row.estimateAcsNextMonth),
    },
    {
      key: "estimateAcsNext2Month",
      label: "Estimated ACS Next 2 Month",
      width: 120,
      numeric: true,
      render: (row) => showValue(row.estimateAcsNext2Month),
    },
    {
      key: "lessonStudied",
      label: "Lesson Studied",
      width: 110,
      numeric: true,
      render: (row) => showValue(row.lessonStudied),
    },
    {
      key: "finalFillDate",
      label: "Final date to fill student of current Module",
      width: 160,
      render: (row) => (
        <span
          style={
            isPastFinalFillDate(row.finalFillDate)
              ? { color: "#d32f2f", fontWeight: 600 }
              : undefined
          }
        >
          {showValue(row.finalFillDate)}
        </span>
      ),
    },
    {
      key: "csoInCharge",
      label: "EC in charge",
      width: 200,
      render: (row) => showValue(row.csoInCharge),
    },
    {
      key: "teacher",
      label: "Teacher(s)",
      width: 200,
      render: (row) => showArray(row.teacher),
    },
    {
      key: "eppThisMonth",
      label: "This Month EPP",
      width: 110,
      numeric: true,
      render: (row) => showValue(row.eppThisMonth),
    },
    {
      key: "eppNextMonth",
      label: "Next Month EPP",
      width: 110,
      numeric: true,
      render: (row) => showValue(row.eppNextMonth),
    },
    {
      key: "eppT8",
      label: "Next 2 Month EPP",
      width: 110,
      numeric: true,
      render: (row) => showValue(row.eppT8),
    },
    {
      key: "recommendToClose",
      label: "Recommend to close",
      width: 130,
      numeric: true,
      render: (row) => renderRecommendToClose(row.recommendToClose),
    },
  ]

  const activeScheduleColumns = scheduleColumns.filter((col) =>
    checkExistColumn(col.key)
  )
  const scheduleGroupWidth = activeScheduleColumns.reduce(
    (sum, col) => sum + col.width,
    0
  )
  const scheduleGridTemplate = activeScheduleColumns
    .map((col) => `${col.width}px`)
    .join(" ")

  const sortedRows = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return sortClassSituationRows(filteredRows)
    // Level names have no alphabetical meaning, so this column always uses the level rule.
    if (sortConfig.key === "classLevel") {
      const byLevel = sortClassSituationRows(filteredRows)
      return sortConfig.direction === "asc" ? byLevel : byLevel.reverse()
    }
    const dir = sortConfig.direction === "asc" ? 1 : -1
    return [...filteredRows]
      .map((row, idx) => ({ row, idx }))
      .sort((a, b) => {
        const av = getSortableValue(a.row, sortConfig.key)
        const bv = getSortableValue(b.row, sortConfig.key)
        if (av === bv) return a.idx - b.idx
        return av > bv ? dir : -dir
      })
      .map((item) => item.row)
  }, [filteredRows, sortConfig])

  const handleOpenNote = (event, rowId) => {
    setAnchorEl(event.currentTarget)
    setActiveRowId(rowId)
    setNewNote("")
  }

  const handleCloseNote = () => {
    setAnchorEl(null)
    setActiveRowId(null)
    setNewNote("")
  }

  // ✅ Rewritten add note (works with rows state)
  const handleAddNote = async () => {
    triggerIfNew()
    const text = newNote.trim()
    if (!text || !activeRowId) return

    const activeRow = rows.find((r) => r.id === activeRowId)
    try {
      if (typeof onAddNote === "function") {
        await onAddNote(text, activeRow)
      }
    } catch (error) {
      return
    }

    const note = {
      id: crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2),
      text,
      timestamp: new Date().toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
      user: "Me",
      rowId: activeRowId,
    }

    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== activeRowId) return r
        return { ...r, notes: [note] }
      })
    )

    setNewNote("")
  }

  // Rendered as the first body row (directly under the header) rather than
  // at the bottom of the table.
  const summaryRow = summary ? (
    <div className="body-row summary-row">
      {checkExistColumn("className") && (
        <div className="col hours w-180 class-name-col p-sticky-left">
          <span className="summary-row__title">
            {summary.summaryLabel || "TOTAL / AVERAGE"}
          </span>
        </div>
      )}
      {checkExistColumn("no") && (
        <div className="col hours w-50">
          {!checkExistColumn("className") && (
            <span className="summary-row__title">
              {summary.summaryLabel || "TOTAL / AVERAGE"}
            </span>
          )}
        </div>
      )}
      {checkExistColumn("center") && (
        <div className="col hours w-150" />
      )}
      {checkExistColumn("classLevel") && <div className="col hours w-100" />}
      {!visibleModuleKeys.length ? null : openModule ? (
        <div className="col module-data" style={fixedWidthStyle(moduleGroupWidth)} />
      ) : (
        <div className="col collapsed w-100" />
      )}
      {activeScheduleColumns.length > 0 &&
        (openSchedule ? (
          <div className="col schedule-data" style={fixedWidthStyle(scheduleGroupWidth)}>
            {activeScheduleColumns.map((col) => {
              const metric = SCHEDULE_SUMMARY[col.key]
              return (
                <div
                  key={col.key}
                  className={`cell ${col.numeric ? "num" : ""}`}
                  style={fixedWidthStyle(col.width)}
                >
                  {metric ? (
                    <SummaryMetric kind={metric.kind} value={summary[metric.key]} />
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="col collapsed w-200" />
        ))}
      {!eventColumns.length ? null : openEvent ? (
        <div className="col event-data" style={{ ...fixedWidthStyle(eventGroupWidth), ...eventGrid }}>
          {eventColumns.map((col) => (
            <div key={col.key} />
          ))}
        </div>
      ) : (
        <div className="col collapsed w-200" />
      )}
      {!visibleWeeks.length ? null : openByWeek ? (
        <div
          className="col module-data-week"
          style={{ ...fixedWidthStyle(weekGroupWidth), gridTemplateColumns: weekGridTemplate }}
        >
          {visibleWeeks.map((week) => (
            <div key={week.key} className="block" style={weekLeafGrid(week)}>
              {week.leaves.map((leaf) => (
                <div key={leaf.label}>
                  {leaf.summary ? (
                    <SummaryMetric kind={leaf.summary.kind} value={summary[leaf.summary.key]} />
                  ) : null}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="col collapsed w-250" />
      )}
      {checkExistColumn("bookNeeded") && (
        <div className="col hours w-150 col-pink num">
          <div className="summary-stack">
            <SummaryMetric kind="total" value={summary.totalNwBookNeeded} hint="Next week" />
            <SummaryMetric kind="total" value={summary.totalNmBookNeeded} hint="Next month" />
          </div>
        </div>
      )}
      {checkExistColumn("koc") && <div className="col hours w-150" />}
      {checkExistColumn("notes") && <div className="col hours w-100" />}
    </div>
  ) : null

  return (
    <>
      {activeFilterCount > 0 && (
        <div className="filters-toolbar">
          <span className="filters-toolbar__label">
            {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""} applied
          </span>
          <button type="button" className="filters-toolbar__clear" onClick={clearAllFilters}>
            <X size={12} />
            Clear filters
          </button>
        </div>
      )}
      <HScroll className="class-table">
        <div className="table-header">
          <div className="header-row p-sticky-top">
          {/* Class name — always the first (pinned) column */}
          {checkExistColumn("className") && (
            <div className="col hours w-180 class-name-col p-sticky-left p-sticky-top">
              {renderHeaderLabel("Class name", "className")}
            </div>
          )}
          {/* No / Center only show when the Class code column isn't merged */}
          {checkExistColumn("no") && (
            <div className="col hours w-50">
              {renderHeaderLabel("No", "no")}
            </div>
          )}
          {checkExistColumn("center") && (
            <div className="col hours w-150 ">
              {renderHeaderLabel("Center", "center")}
            </div>
          )}
          {checkExistColumn("classLevel") && (
            <div className="col hours w-100   class-level">
              {renderHeaderLabel("Class level", "classLevel")}
            </div>
          )}

          {!visibleModuleKeys.length ? null : openModule ? (
            <div className="col module-header" style={fixedWidthStyle(moduleGroupWidth)}>
              <div className="main bg-module-1">
                <span title="Module">Module</span>
                &nbsp;
                <div onClick={() => setOpenModule(!openModule)}>
                  <Expend />
                </div>
              </div>

              <div className="sub" style={moduleGrid}>
                {visibleModuleKeys.map((key) => key.toUpperCase()).map((m) => (
                  <div className="sub-col" key={m}>
                    <div className="name bg-module-2">{m}</div>
                    <div className="dates bg-module-3">
                      <div>Start </div>
                      <div>Finish </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="col collapsed-header w-100 bg-module-1 center">
              <div className="main">
                <span title="Module">Module</span>
              </div>
              &nbsp;
              <div onClick={() => setOpenModule(!openModule)}>
                <Collapsed />
              </div>
            </div>
          )}

          {activeScheduleColumns.length > 0 &&
            (openSchedule ? (
              <div
                className="col schedule-header"
                style={fixedWidthStyle(scheduleGroupWidth)}
              >
                <div className="main bg-schedule-1">
                  <span title="Schedule">Schedule</span>
                  &nbsp;
                  <div onClick={() => setOpenSchedule(!openSchedule)}>
                    <Expend />
                  </div>
                </div>
                <div
                  className="sub bg-schedule-2"
                  style={{ gridTemplateColumns: scheduleGridTemplate }}
                >
                  {activeScheduleColumns.map((col) => (
                    <div className="sub-col" key={col.key}>
                      {renderHeaderLabel(col.label, col.key)}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="col collapsed-header w-200 bg-schedule-1">
                <div className="main">
                  <span title="Schedule">Schedule</span>
                </div>
                &nbsp;
                <div onClick={() => setOpenSchedule(!openSchedule)}>
                  <Collapsed />
                </div>
              </div>
            ))}

          {/* Class situation event Header */}
          {!eventColumns.length ? null : openEvent ? (
            <div className="col event-header col-event" style={fixedWidthStyle(eventGroupWidth)}>
              <div className="main bg-event-1">
                <span title="Class situation event">Class situation event</span>
                &nbsp;
                <div onClick={() => setOpenEvent(!openEvent)}>
                  <Expend />
                </div>
              </div>
              <div className="sub bg-event-2" style={eventGrid}>
                {eventColumns.map((col) => (
                  <div className="sub-col center" key={col.key}>
                    {col.label}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="col collapsed-header w-200 bg-event-1">
              <div className="main">
                <span title="Class situation event">Class situation event</span>
              </div>
              &nbsp;
              <div onClick={() => setOpenEvent(!openEvent)}>
                <Collapsed />
              </div>
            </div>
          )}

          {/* Class situation week */}
          {!visibleWeeks.length ? null : openByWeek ? (
            <div className="col module-header" style={fixedWidthStyle(weekGroupWidth)}>
              <div className="main bg-week">
                <span title="Class Situation (Week)">Class Situation (Week)</span>
                &nbsp;
                <div onClick={() => setOpenByWeek(!openByWeek)}>
                  <Expend />
                </div>
              </div>
              <div className="sub" style={{ gridTemplateColumns: weekGridTemplate }}>
                {visibleWeeks.map((m) => (
                  <div className="sub-col" key={m.key}>
                    <div
                      className="name bg-week-10"
                      style={{
                        background:
                          m.key === "thisWeek"
                            ? "#FFE3B1"
                            : m.key === "nextWeek"
                            ? "#C3FFDE"
                            : undefined,
                      }}
                    >
                      {m.label}
                    </div>
                    <div
                      className={`week bg-week-2 ${m.key}`}
                      style={{
                        ...weekLeafGrid(m),
                        background:
                          m.key === "thisWeek"
                            ? "#FFF4E0"
                            : m.key === "nextWeek"
                            ? "#E5FFF1"
                            : undefined,
                      }}
                    >
                      {m.leaves.map((leaf) => (
                        <div key={leaf.label}>{leaf.label}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="col collapsed-header w-250 bg-week">
              <div className="main">
                <span title="Class Situation (Week)">Class Situation (Week)</span>
                &nbsp;
                <div onClick={() => setOpenByWeek(!openByWeek)}>
                  <Collapsed />
                </div>
              </div>
            </div>
          )}

          {checkExistColumn("bookNeeded") && (
            <div className="col hours w-150">
              {renderHeaderLabel("Book Needed", "bookNeeded")}
            </div>
          )}

          {checkExistColumn("koc") && (
            <div className="col hours w-150">
              {renderHeaderLabel("KOC", "koc")}
            </div>
          )}
          {checkExistColumn("notes") && (
            <div className="col hours w-100 p-sticky-right notes-header">
              {renderHeaderLabel("Notes", "notes", { sortable: false })}
            </div>
          )}
        </div>
      </div>

      <div className="table-body">
        {summaryRow}
        {sortedRows.map((row, i) => {
          const rowColor = getComputedRowColor(row)
          return (
          <div
            className={`body-row ${rowColor ? `row-highlight-${rowColor}` : ""}`}
            key={row.id ?? i}
            style={getRowStyle(row)}
            title={getRowColorTitle(rowColor)}
          >
            {checkExistColumn("className") && (
              <div className="col hours w-180 class-name-col p-sticky-left">
                {renderClassNameLink(row.className, row.classId)}
              </div>
            )}
            {checkExistColumn("no") && (
              <div className="col hours w-50 ">{showValue(row.no)}</div>
            )}
            {checkExistColumn("center") && (
              <div className="col hours w-150">{showValue(row.center)}</div>
            )}
            {checkExistColumn("classLevel") && (
              <div className="col hours w-100  ">
                <b>{showValue(row.classLevel)}</b>
              </div>
            )}
            {!visibleModuleKeys.length ? null : openModule ? (
              <div
                className="col module-data"
                style={{
                  ...fixedWidthStyle(moduleGroupWidth),
                  ...moduleGrid,
                }}
              >
                {visibleModuleKeys.map((m) => {
                  const isInCurrentMonthFinish = isInCurrentMonth(
                    row.modules?.[m]?.finish
                  )
                  const isInCurrentMonthStart = isInCurrentMonth(
                    row.modules?.[m]?.start
                  )

                  return (
                    <div className="block" key={m}>
                      <div
                        className={isInCurrentMonthStart ? "current-month" : ""}
                      >
                        {showValue(row.modules?.[m]?.start)}
                      </div>
                      <div
                        className={
                          isInCurrentMonthFinish ? "current-month" : ""
                        }
                      >
                        {showValue(row.modules?.[m]?.finish)}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="col collapsed w-100">{showValue(row.modules?.currentModule)}</div>
            )}
            {activeScheduleColumns.length > 0 &&
              (openSchedule ? (
                <div
                  className="col schedule-data"
                  style={fixedWidthStyle(scheduleGroupWidth)}
                >
                  {activeScheduleColumns.map((col) => (
                    <div
                      key={col.key}
                      className={`cell ${col.cellClass ?? ""} ${col.numeric ? "num" : ""}`}
                      style={fixedWidthStyle(col.width)}
                    >
                      {col.render(row)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="col collapsed w-200">-</div>
              ))}

            {!eventColumns.length ? null : openEvent ? (
              <div className="col event-data" style={{ ...fixedWidthStyle(eventGroupWidth), ...eventGrid }}>
                {eventColumns.map((col) => (
                  <div key={col.key}>{showValue(col.value(row))}</div>
                ))}
              </div>
            ) : (
              <div className="col collapsed w-200">-</div>
            )}

            {!visibleWeeks.length ? null : openByWeek ? (
              <div
                className="col module-data-week"
                style={{ ...fixedWidthStyle(weekGroupWidth), gridTemplateColumns: weekGridTemplate }}
              >
                {visibleWeeks.map((m) => (
                  <div key={m.key} className="block" style={weekLeafGrid(m)}>
                    {m.leaves.map((leaf) => (
                      <div key={leaf.label}>{leaf.value(row?.[m.key])}</div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="col collapsed w-250">-</div>
            )}

            {checkExistColumn("bookNeeded") && (
              <div className="col hours w-150 col-pink num">{showValue(row.bookNeeded)}</div>
            )}
            {checkExistColumn("koc") && (
              <div className="col hours w-150">{showValue(row.koc)}</div>
            )}

            {checkExistColumn("notes") && (
              <NotesCell
                rowId={row.id}
                initialValue={row.nextMonth?.notes}
                handleOpenNote={handleOpenNote}
                rowNotes={row.notes}
              />
            )}
          </div>
          )
        })}
        {sortedRows.length === 0 && rows.length > 0 && (
          <div className="table-empty-state">
            <div className="table-empty-state__title">No results found</div>
            <div className="table-empty-state__subtitle">
              Try adjusting or clearing your filters.
            </div>
            <button
              type="button"
              className="table-empty-state__clear"
              onClick={clearAllFilters}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* ✅ Notes Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseNote}
        anchorOrigin={{ vertical: "center", horizontal: "left" }}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 360,
            p: 2,
            borderRadius: 4,
            overflow: "visible",
            border: "1px solid",
            borderColor: "rgba(148,163,184,0.25)",
            boxShadow:
              "0 20px 25px -5px rgba(0,0,0,0.08), 0 10px 10px -5px rgba(0,0,0,0.04)",
            bgcolor: "background.paper",
          },
        }}
      >
        <MDTypography sx={{ fontWeight: "bold", fontSize: 14 }}>
          Notes History
        </MDTypography>

        <MDBox
          sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          {/* ✅ Render real notes */}
          {activeNotes.length ? (
            activeNotes.map((n) => (
              <MDBox
                key={n.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.06)"
                      : "#F6F8FB",
                  border: "1px solid",
                  borderColor: "rgba(148,163,184,0.18)",
                }}
              >
                <MDTypography sx={{ fontSize: 14 }}>{n.text}</MDTypography>

                <MDBox
                  sx={{
                    mt: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <MDTypography
                    sx={{ opacity: 0.7, fontSize: 12, fontWeight: 600 }}
                  >
                    {n.user || "-"}
                  </MDTypography>
                  <MDTypography
                    sx={{ opacity: 0.7, fontSize: 12, fontWeight: 600 }}
                  >
                    {n.timestamp || "-"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            ))
          ) : (
            <MDTypography sx={{ fontSize: 13, opacity: 0.7 }}>
              No notes yet.
            </MDTypography>
          )}

          {/* Add note row */}
          <TextField
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddNote()
            }}
            placeholder="Add note..."
            size="small"
            fullWidth
            InputProps={{
              sx: { borderRadius: 2, paddingRight: 0 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="add note"
                    edge="end"
                    size="small"
                    onClick={handleAddNote}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      borderColor: "divider",
                    }}
                  >
                    <Plus
                      size={16}
                      color={activeNotes.length ? "secondary" : "primary"}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </MDBox>
      </Popover>
      </HScroll>
      <TableLegend />
    </>
  )
}

function NotesCell({ initialValue, rowId, handleOpenNote, rowNotes }) {
  const [isEditing, setIsEditing] = useState(false)
  const [note, setNote] = useState(initialValue)

  const handleEdit = () => setIsEditing(true)

  const handleChange = (e) => setNote(e.target.value)

  const handleSave = () => {
    setIsEditing(false)
  }

  const isActive = initialValue || (rowNotes && rowNotes.length > 0)
  const noteCount = rowNotes?.length || 0

  return (
    <div className="col hours w-100 col-notes p-sticky-right">
      {isEditing && (
        <input
          type="text"
          name="note"
          value={note}
          onChange={handleChange}
          className="input-table"
        />
      )}

      <div className="btn-edit">
        {isEditing ? (
          <div className="btn-save" onClick={handleSave}>
            Save
          </div>
        ) : (
          <div
            onClick={(e) => handleOpenNote(e, rowId)}
            className={`icon-edit ${isActive ? "active" : ""}`}
          >
            <MessageSquare size={18} color={isActive ? "#667eea" : "#718096"} />
            {noteCount > 0 && <span className="note-badge">{noteCount}</span>}
          </div>
        )}
      </div>
    </div>
  )
}
